import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Box, Theme, CircularProgress } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import videojs from 'video.js';
import { I18n } from '@lingui/core';
import { isiOS, isSafari } from '../lib/Device';
import { ContentVideoCaptionSchema } from '../definitions/schemas';
import 'videojs-hotkeys';
import 'videojs-vjsdownload';

// styles
import 'video.js/dist/video-js.min.css';
import 'videojs-vjsdownload/dist/videojs-vjsdownload.css';
import { useI18n } from '../lib/hook/useI18n';
import useAppMode from '../lib/hook/config/useAppMode';

const useStyles = makeStyles((theme: Theme) => ({
  fixedHeight: {
    objectFit: 'cover',
    maxHeight: theme.spacing(44), // 352px
  },
  video: {
    height: '100%',
    overflow: 'hidden',
    // fix - overflow: hidden added to avoid glitching videos when scrollbars shows up
    width: '100%',
  },
  videoRounded: {
    borderRadius: theme.spacing(1), // 10px
  },
  videoContainer: {
    '& .video-js': {
      background: 'white',
    },
    '& .video-js .vjs-tech': {
      height: 'calc(100% - 2px)',
      objectFit: 'cover',
    },
    '& .video-js .vjs-poster': {
      backgroundSize: 'cover',
    },
    '& .video-js .vjs-current-time, .video-js .vjs-time-divider, .video-js .vjs-duration':
      {
        display: 'block',
      },
    '& .vjs-time-divider': {
      minWidth: 'auto',
      padding: '0',
    },
    '&, .vjs-playback-rate .vjs-menu': {
      width: '50px',
      height: '90px',
      marginBottom: '30px',
    },
    '&, .vjs-playback-rate .vjs-menu .vjs-menu-content': {
      width: '100%',
      height: '100%',
      bottom: '0',
    },
    height: '100%',
    position: 'relative',
  },
  videoContainerRounded: {
    '& .video-js .vjs-tech': {
      borderRadius: theme.spacing(1),
    },
    '& .video-js .vjs-poster': {
      borderRadius: theme.spacing(1),
    },
    '& .video-js .vjs-control-bar': {
      borderBottomLeftRadius: theme.spacing(1),
      borderBottomRightRadius: theme.spacing(1),
    },
  },
  loading: {
    position: 'absolute',
    left: '45%',
    top: '45%',
    zIndex: 1,
  },
}));

interface VideoPlayerClasses {
  container?: string;
  video?: string;
}

interface VideoPlayerLabels {
  backgroundLabel: string;
  captionsLabel: string;
  captionsOffLabel: string;
  captionsSettingsLabel: string;
  doneLabel: string;
  downloadLabel: string;
  exitFullscreenLabel: string;
  fontFamilyLabel: string;
  fontSizeLabel: string;
  fullscreenLabel: string;
  muteLabel: string;
  pauseLabel: string;
  playbackRateLabel: string;
  playLabel: string;
  replayLabel: string;
  resetLabel: string;
  textEdgeStyleLabel: string;
  textLabel: string;
  unmuteLabel: string;
  windowLabel: string;
}

const DOWNLOAD_BUTTON_NAME = 'downloadButton';

const getVideoJSPlayerOptions = (
  downloadable: boolean,
  labels: VideoPlayerLabels,
  i18n: I18n,
  downloadUrl?: string
) => {
  const canDownload = downloadable && !!downloadUrl;

  // this array determines which controls and in which order they're
  // displayed in the UI, changing the order of this array also changes
  // the order of the controls
  const children = [
    'playToggle',
    'currentTimeDisplay',
    'timeDivider',
    'durationDisplay',
    'progressControl',
    'captionsButton',
    'playbackRateMenuButton',
    'volumePanel',
    'fullscreenToggle',
  ];
  if (canDownload) {
    // inject before fullscreenToggle
    children.splice(8, 0, DOWNLOAD_BUTTON_NAME);
  }

  const {
    backgroundLabel,
    captionsLabel,
    captionsOffLabel,
    captionsSettingsLabel,
    doneLabel,
    downloadLabel,
    exitFullscreenLabel,
    fontFamilyLabel,
    fontSizeLabel,
    fullscreenLabel,
    muteLabel,
    pauseLabel,
    playbackRateLabel,
    playLabel,
    replayLabel,
    resetLabel,
    textEdgeStyleLabel,
    textLabel,
    unmuteLabel,
    windowLabel,
  } = labels;

  const options = {
    language: i18n.locale,
    languages: {
      [i18n.locale]: {
        'captions off': captionsOffLabel,
        'captions settings': captionsSettingsLabel,
        'Font Family': fontFamilyLabel,
        'Font Size': fontSizeLabel,
        'Non-Fullscreen': exitFullscreenLabel,
        'Playback Rate': playbackRateLabel,
        'Text Edge Style': textEdgeStyleLabel,
        Background: backgroundLabel,
        Captions: captionsLabel,
        Done: doneLabel,
        Fullscreen: fullscreenLabel,
        Mute: muteLabel,
        Pause: pauseLabel,
        Play: playLabel,
        Replay: replayLabel,
        Reset: resetLabel,
        Text: textLabel,
        Unmute: unmuteLabel,
        Window: windowLabel,
      },
    },
    aspectRatio: '16:9',
    autoplay: false,
    controls: true,
    controlBar: {
      children,
      volumePanel: {
        inline: false,
        volumeControl: {
          vertical: true,
        },
      },
    },
    errorDisplay: false,
    // metadata in safari don't load the first frame and display a black poster
    // we need to change to auto for load the first frame and
    // let safari decide how to load the video
    preload: isSafari() || isiOS() ? 'auto' : 'metadata',
    playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
    plugins: {
      hotkeys: {
        playPauseKey: (e: any) => e.which === 8,
      },
    },
  } as videojs.PlayerOptions;

  // the download button is only shown when a config is provided
  if (canDownload) {
    options!.plugins!.vjsdownload = {
      // the plugin requires this prop to be have URL (all uppercase)
      downloadURL: downloadUrl,
      name: DOWNLOAD_BUTTON_NAME,
      // inject translations (i18n context is only available within the component)
      textControl: downloadLabel,
    };
  }

  return options;
};

export interface VideoPlayerProps {
  autoPlay?: boolean;
  captions?: ContentVideoCaptionSchema[];
  classes?: VideoPlayerClasses;
  cover?: string;
  downloadable?: boolean;
  downloadUrl?: string;
  fixedHeight?: boolean;
  onLoaded?: () => void;
  onPlay?: () => void;
  rounded?: boolean;
  type?: string;
  url: string;
  urlLoading?: boolean;
}

const VideoPlayer = React.forwardRef<HTMLVideoElement, VideoPlayerProps>(
  (props: VideoPlayerProps, ref) => {
    const { t, i18n } = useI18n();
    const classes = useStyles();

    const {
      autoPlay = false,
      captions,
      classes: classesProp,
      cover,
      downloadable = false,
      downloadUrl,
      fixedHeight = true,
      onLoaded = () => {},
      onPlay,
      rounded = true,
      type,
      url,
      urlLoading = false,
    } = props;

    const [loading, setLoading] = useState(true);

    const playedRef = useRef(false);

    const initialRef = useRef(null);

    const videoRef = (ref ||
      initialRef) as React.MutableRefObject<HTMLVideoElement>;

    const labels: VideoPlayerLabels = {
      backgroundLabel: t('video.player.captions_settings.background'),
      captionsLabel: t('video.player.captions'),
      captionsOffLabel: t('video.player.captions_off'),
      captionsSettingsLabel: t('video.player.captions_settings'),
      doneLabel: t('video.player.captions_settings.done'),
      downloadLabel: t('video.player.download'),
      exitFullscreenLabel: t('video.player.exit_fullscreen'),
      fontFamilyLabel: t('video.player.captions_settings.font_family'),
      fontSizeLabel: t('video.player.captions_settings.font_size'),
      fullscreenLabel: t('video.player.fullscreen'),
      muteLabel: t('video.player.mute'),
      pauseLabel: t('video.player.pause'),
      playbackRateLabel: t('video.player.playback_rate'),
      playLabel: t('video.player.play'),
      replayLabel: t('video.player.replay'),
      resetLabel: t('video.player.captions_settings.reset'),
      textEdgeStyleLabel: t('video.player.captions_settings.text_edge_style'),
      textLabel: t('video.player.captions_settings.text'),
      unmuteLabel: t('video.player.unmute'),
      windowLabel: t('video.player.captions_settings.window'),
    };

    const { isEmbeddedMode } = useAppMode();

    useEffect(() => {
      // flag to avoid warnings because there's an update on the unmounted component
      let active = true;

      // Don't initialize video player while URL is still loading
      if (urlLoading) {
        return;
      }

      // options must be generated for each vjsPlayer to avoid nested/shallow changes
      const options = getVideoJSPlayerOptions(
        downloadable && !isEmbeddedMode,
        labels,
        i18n,
        downloadUrl
      );

      const vjsPlayer = videojs(videoRef.current, options, () => {
        vjsPlayer.autoplay(autoPlay);
        vjsPlayer.bigPlayButton.hide();
        vjsPlayer.hotkeys({ alwaysCaptureHotkeys: true });

        const beforeListener = (listener: (ev: any) => void) => (ev: any) => {
          // When autoPlay=true the video is played even if it not mounted
          if (!document.body.contains(ev.target)) {
            vjsPlayer.pause();
          }
          listener(ev);
        };

        // remover loader and display the play button when the video has loaded
        vjsPlayer.on(
          'loadedmetadata',
          beforeListener(() => {
            if (!active) {
              return;
            }

            setLoading(false);
            vjsPlayer.bigPlayButton.show();
            onLoaded();
          })
        );
      });

      return () => {
        active = false;
      };
      /**
       * Dependencies not listed in order to avoid recreating vjsPlayer:
       * 'autoPlay', 'downloadable' and 'onLoaded'
       */
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoRef, cover, url, i18n.locale, urlLoading]);

    const onVideoPlayed = () => {
      if (!playedRef.current && onPlay) {
        playedRef.current = true;
        onPlay();
      }
    };

    const renderCaptions = () =>
      captions?.map((caption) => (
        <track
          key={`caption-${caption.url}`}
          kind="captions"
          label={caption.language}
          lang={caption.languageCode}
          src={caption.url}
        />
      ));

    const loadingComponent = (loading || urlLoading) && (
      <CircularProgress color="secondary" className={classes.loading} />
    );

    return (
      <Box
        className={classNames(
          classes.videoContainer,
          {
            [classes.videoContainerRounded]: rounded,
          },
          classesProp?.container
        )}
      >
        {loadingComponent}
        {!urlLoading && (
          <div data-vjs-player>
            <video
              ref={videoRef}
              crossOrigin="anonymous"
              key={url}
              autoPlay={autoPlay}
              className={classNames(
                classes.video,
                'video-js vjs-big-play-centered vjs-vjsdownload',
                {
                  [classes.fixedHeight]: fixedHeight,
                  [classes.videoRounded]: rounded,
                },
                classesProp?.video
              )}
              controls
              controlsList={
                !downloadable || isEmbeddedMode ? 'nodownload' : undefined
              }
              onContextMenu={(e) => {
                if (!downloadable) e.preventDefault();
              }}
              onPlay={onVideoPlayed}
              poster={cover}
              style={{ objectFit: 'cover' }}
            >
              <source src={url} type={type} />
              {captions && renderCaptions()}
            </video>
          </div>
        )}
      </Box>
    );
  }
);

export default VideoPlayer;
